import './settings-leaf.css';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MoneyInput } from '../money-input.tsx';
import { companyApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { usePlanFeature } from '../plan-gate.tsx';
import { Alert, ConfirmDialog, Select, TextInput } from '../ui.tsx';
import { jumpToSettingsSection } from '../settings-nav.tsx';
import ServiceModeChoice from '../service-mode-choice.tsx';
import { timezoneOptions } from '../../forms/timezones.ts';
import { currencyOptions } from '../../forms/currencies.ts';
import { DEFAULT_LANGUAGE, normalizeLanguage, SUPPORTED_LANGUAGES } from '@shared/constants/languages.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import {
  applyDateTimeFormatPrefs,
  COMMON_LOCALES,
  DEFAULT_LOCALE,
  localeLabel,
  parseDateFormat,
  parseTimeFormat,
  type DateFormatStyle,
  type TimeFormatStyle,
} from '@shared/locale-format.ts';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * Who the company is: name, contact details, address, and the three regional
 * choices (timezone, outbound language, currency) that everything downstream
 * reads. The logo lives here too — it uploads on selection through its own
 * endpoint, independent of the Save button.
 */

const snapshot = (company: Company) => ({
  name: company.name,
  legalName: company.legalName,
  email: company.email,
  phone: company.phone,
  website: company.website,
  addressLine1: company.addressLine1,
  addressLine2: company.addressLine2,
  city: company.city,
  state: company.state,
  postalCode: company.postalCode,
  timezone: company.timezone,
  language: company.language,
  currencyCode: company.currencyCode,
  /*
    Null until somebody saves a locale. The picker shows en-US in that case,
    but the payload omits it: saving the name or the address must not lock a
    company that still follows the browser into en-US.
  */
  locale: company.locale,
  dateFormat: company.dateFormat,
  timeFormat: company.timeFormat,
  /*
    Null when nobody ever answered the question, rather than the residential
    default the rest of the app reads: the picker then shows NOTHING selected,
    which is both the honest state and the only way the answer can be given.
    Seeded with the default instead, an owner who read the question and wanted
    to keep residential would be clicking an option that was already selected —
    no change, so no dirty draft, so a disabled Save and a setup-guide step
    they cannot complete without first picking a mode they do not want.
  */
  serviceMode: company.serviceModeChosenAt ? company.serviceMode : null,
  laborCostCentsPerHour: company.laborCostCentsPerHour,
});

const toPayload = (draft: ReturnType<typeof snapshot>) => ({
  name: draft.name,
  legalName: draft.legalName ?? '',
  email: draft.email ?? '',
  phone: draft.phone ?? '',
  website: draft.website ?? '',
  addressLine1: draft.addressLine1 ?? '',
  addressLine2: draft.addressLine2 ?? '',
  city: draft.city ?? '',
  state: draft.state ?? '',
  postalCode: draft.postalCode ?? '',
  timezone: draft.timezone,
  // Normalized rather than asserted. `companies.language` is a plain TEXT
  // column, so a row can hold a code this build has no translations for —
  // written by a newer deploy and then rolled back, or edited by hand in
  // support. Sending it back would fail the server's enum and take every
  // other field on this card down with it. Falling back to what the picker
  // is actually showing keeps the card usable and self-consistent.
  language: normalizeLanguage(draft.language) ?? DEFAULT_LANGUAGE,
  currencyCode: draft.currencyCode,
  // Omitted while never chosen. Sending the en-US the picker displays would
  // record a choice the owner did not make.
  ...(draft.locale ? { locale: draft.locale } : {}),
  dateFormat: draft.dateFormat,
  timeFormat: draft.timeFormat,
  // Omitted while unanswered, so saving an address on a company that has
  // never been asked does not answer the question on its behalf — the server
  // stamps an answer for every mode it is sent, deliberately.
  ...(draft.serviceMode ? { serviceMode: draft.serviceMode } : {}),
  laborCostCentsPerHour: draft.laborCostCentsPerHour,
});

export default function CompanyDetailsCard() {
  const { t } = useTranslation();
  const { company, refresh, can } = useSession();
  const form = useCompanyCardForm(snapshot, toPayload);
  const hasTeamPay = usePlanFeature('job_profit');
  const canViewTeamPay = hasTeamPay && can('reports:read');
  const [logoError, setLogoError] = useState<string | null>(null);
  const [confirmCurrency, setConfirmCurrency] = useState(false);

  const uploadLogo = useMutation({
    mutationFn: (file: File) => companyApi.uploadLogo(file),
    onSuccess: () => {
      setLogoError(null);
      void refresh();
    },
    onError: (caught) =>
      setLogoError(caught instanceof ApiRequestError ? caught.message : t('settings.logoUploadError')),
  });

  const draft = form.draft;
  if (!draft) return null;

  const previewLocale = draft.locale ?? DEFAULT_LOCALE;
  let previewMoney = draft.currencyCode;
  let previewDate = '2026-11-08';
  try {
    previewMoney = formatCents(123456, draft.currencyCode, previewLocale);
    previewDate = new Intl.DateTimeFormat(
      previewLocale,
      applyDateTimeFormatPrefs({ dateStyle: 'medium', timeStyle: 'short', timeZone: draft.timezone }, draft),
    ).format(new Date('2026-11-08T18:30:00.000Z'));
  } catch {
    // A timezone this runtime rejects still leaves the fields usable.
  }

  const localeTags = [...COMMON_LOCALES] as string[];
  if (draft.locale && !localeTags.includes(draft.locale)) localeTags.unshift(draft.locale);

  function saveDetails() {
    if (company && draft && draft.currencyCode !== company.currencyCode) {
      setConfirmCurrency(true);
      return;
    }
    form.submit();
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="company-heading">
      <div className="work-section-heading">
        <h2 id="company-heading" className="mb-4 text-lg font-semibold">
          {t('settings.companyHeading')}
        </h2>
      </div>

      {/*
        What kind of cleaning this company does — first on the card, above the
        name and the address, because it is the one setting here that changes
        what the rest of the app does: which starter libraries the company
        carries, and what a new quote defaults to. Buried under the address it
        was found by the owners who already knew to look for it.

        Switching commercial work on also seeds a commercial room & task
        library, which is why the hint says so before anybody presses Save.
        Nothing is selected for a company nobody ever asked — see `snapshot`.
      */}
      <ServiceModeChoice
        label={t('settings.serviceModeLabel')}
        hint={t('settings.serviceModeHint')}
        value={draft.serviceMode}
        disabled={form.readOnly}
        onChange={(mode) => form.set('serviceMode', mode)}
      />

      <div className="grid gap-x-4 sm:grid-cols-2">
        <TextInput
          label={t('settings.companyName')}
          required
          value={draft.name}
          disabled={form.readOnly}
          error={form.fieldErrors.name}
          onChange={(event) => form.set('name', event.target.value)}
        />
        <TextInput
          label={t('settings.legalName')}
          hint={t('settings.legalNameHint')}
          value={draft.legalName ?? ''}
          disabled={form.readOnly}
          onChange={(event) => form.set('legalName', event.target.value)}
        />
        <TextInput
          label={t('settings.email')}
          type="email"
          value={draft.email ?? ''}
          disabled={form.readOnly}
          error={form.fieldErrors.email}
          onChange={(event) => form.set('email', event.target.value)}
        />
        <TextInput
          label={t('settings.phone')}
          type="tel"
          value={draft.phone ?? ''}
          disabled={form.readOnly}
          onChange={(event) => form.set('phone', event.target.value)}
        />
      </div>

      <TextInput
        label={t('settings.website')}
        type="url"
        placeholder="https://"
        value={draft.website ?? ''}
        disabled={form.readOnly}
        error={form.fieldErrors.website}
        onChange={(event) => form.set('website', event.target.value)}
      />
      <TextInput
        label={t('settings.addressLine1')}
        value={draft.addressLine1 ?? ''}
        disabled={form.readOnly}
        onChange={(event) => form.set('addressLine1', event.target.value)}
      />
      <TextInput
        label={t('settings.addressLine2')}
        value={draft.addressLine2 ?? ''}
        disabled={form.readOnly}
        onChange={(event) => form.set('addressLine2', event.target.value)}
      />
      <div className="grid gap-x-4 sm:grid-cols-3">
        <TextInput
          label={t('settings.city')}
          value={draft.city ?? ''}
          disabled={form.readOnly}
          onChange={(event) => form.set('city', event.target.value)}
        />
        <TextInput
          label={t('settings.state')}
          value={draft.state ?? ''}
          disabled={form.readOnly}
          onChange={(event) => form.set('state', event.target.value)}
        />
        <TextInput
          label={t('settings.postalCode')}
          value={draft.postalCode ?? ''}
          disabled={form.readOnly}
          onChange={(event) => form.set('postalCode', event.target.value)}
        />
        {/*
          Quote dates and the expiry check are evaluated here, not in UTC.
          Without this control a company outside the fallback zone had no
          way to correct it, and quotes written near local midnight took the
          wrong date.
        */}
        <Select
          label={t('settings.timezone')}
          hint={t('settings.timezoneHint')}
          value={draft.timezone}
          disabled={form.readOnly}
          options={timezoneOptions(draft.timezone)}
          onChange={(event) => form.set('timezone', event.target.value)}
        />
        {/*
          The language quote emails, reminders and texts are composed in, and
          the language a brand-new company's starter data was seeded in.
          Changing it affects what is sent from now on; nothing already
          written is rewritten.
        */}
        <Select
          label={t('settings.language')}
          hint={t('settings.languageHint')}
          value={normalizeLanguage(draft.language) ?? DEFAULT_LANGUAGE}
          disabled={form.readOnly}
          options={SUPPORTED_LANGUAGES.map((code) => ({
            value: code,
            label: t(`settings.languageOptions.${code}`),
          }))}
          onChange={(event) => form.set('language', event.target.value)}
        />
        {/*
          The currency every new quote is written in and every card is charged
          in. Existing quotes and payments keep the currency they were created
          with — each one stores its own — so changing this redenominates
          nothing that already happened.
        */}
        <Select
          label={t('settings.currency')}
          hint={t('settings.currencyHint')}
          value={draft.currencyCode}
          disabled={form.readOnly}
          error={form.fieldErrors.currencyCode}
          options={currencyOptions(draft.currencyCode)}
          onChange={(event) => form.set('currencyCode', event.target.value)}
        />
        <Select
          label={t('settings.locale')}
          hint={t('settings.localeHint')}
          value={draft.locale ?? DEFAULT_LOCALE}
          disabled={form.readOnly}
          error={form.fieldErrors.locale}
          options={localeTags.map((tag) => ({ value: tag, label: localeLabel(tag) }))}
          onChange={(event) => form.set('locale', event.target.value)}
        />
        <Select
          label={t('settings.dateFormat')}
          hint={t('settings.dateFormatHint')}
          value={draft.dateFormat ?? ''}
          disabled={form.readOnly}
          options={[
            { value: '', label: t('settings.dateFormatDefault') },
            { value: 'short', label: t('settings.dateFormatShort') },
            { value: 'medium', label: t('settings.dateFormatMedium') },
            { value: 'long', label: t('settings.dateFormatLong') },
          ]}
          onChange={(event) => form.set('dateFormat', parseDateFormat(event.target.value) as DateFormatStyle | null)}
        />
        <Select
          label={t('settings.timeFormat')}
          hint={t('settings.timeFormatHint')}
          value={draft.timeFormat ?? ''}
          disabled={form.readOnly}
          options={[
            { value: '', label: t('settings.timeFormatDefault') },
            { value: '12', label: t('settings.timeFormat12') },
            { value: '24', label: t('settings.timeFormat24') },
          ]}
          onChange={(event) => form.set('timeFormat', parseTimeFormat(event.target.value) as TimeFormatStyle | null)}
        />
        <p className="field-hint mb-4 sm:col-span-3">
          {t('settings.formatPreview', { money: previewMoney, date: previewDate })}
        </p>
      </div>

      {/*
        The hourly backup for Profit by place. Wages and payroll only: Team
        pay is what costs a visit when it is set up, person by person. This
        amount fills in for a company without Team pay, and for someone whose
        pay is missing on a visit. It does not add insurance or the van.

        Commercial work only, because it answers a commercial question: which
        of these standing contracts is still profitable. Left empty, a visit
        Team pay cannot cost shows hours and declines to state a margin.

        Shown to a residential company that already carries a figure, because
        the figure is still in effect. A number doing work behind a field
        nobody can see is the thing this rule exists to prevent, and it is
        also the only way to clear it.

        The mode read here falls through to the STORED one while the draft's is
        null, which is how the card shows a company nobody ever asked (see
        `snapshot`): such a company still has a mode, and one already doing
        commercial work must not lose this field merely because the question
        it was never asked is still open.
      */}
      {(draft.serviceMode ?? company?.serviceMode) !== 'residential' || draft.laborCostCentsPerHour !== null ? (
        <div className="mb-4 max-w-md">
          <div className="max-w-xs">
            <MoneyInput
              currencyCode={draft.currencyCode}
              label={t('settings.laborCostLabel')}
              hint={t('settings.laborCostHint')}
              valueCents={draft.laborCostCentsPerHour}
              disabled={form.readOnly}
              min={0}
              onChangeCents={(cents) => form.set('laborCostCentsPerHour', cents)}
            />
          </div>
          {canViewTeamPay ? (
            <p className="mb-4">
              <a
                href="/settings#settings-teamPay"
                className="font-semibold text-[color:var(--color-brand)] underline"
                onClick={(event) => {
                  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  // Keep this card mounted, including unsaved money and other
                  // edits, while moving scroll and keyboard focus to Team pay.
                  if (jumpToSettingsSection('teamPay')) event.preventDefault();
                }}
              >
                {t('settings.laborCostTeamPayLink')}
              </a>
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="mt-2">
        <label className="field-label" htmlFor="logo-upload">
          {t('settings.logo')}
        </label>
        <p className="field-hint mb-2">{t('settings.logoHint')}</p>
        {logoError ? (
          <div className="mb-2">
            <Alert tone="error">{logoError}</Alert>
          </div>
        ) : null}
        {company?.logoUrl ? (
          <img
            src={company.logoUrl}
            alt={t('settings.logoAlt', { name: draft.name })}
            className="mb-2 max-h-20 rounded border border-[color:var(--color-line)] p-2"
          />
        ) : null}
        <input
          id="logo-upload"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={form.readOnly || uploadLogo.isPending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) uploadLogo.mutate(file);
          }}
        />
      </div>

      {confirmCurrency && company ? (
        <ConfirmDialog
          title={t('settings.currencyChangeTitle')}
          confirmLabel={t('settings.currencyChangeConfirm')}
          confirmVariant="danger"
          initialFocus="cancel"
          onConfirm={() => {
            setConfirmCurrency(false);
            form.submit();
          }}
          onCancel={() => setConfirmCurrency(false)}
        >
          {t('settings.currencyChangeBody', {
            currency: draft.currencyCode,
            previous: company.currencyCode,
          })}
        </ConfirmDialog>
      ) : null}

      <CardSaveFooter form={form} saveLabel={t('settings.saveCompany')} onSubmit={saveDetails} />
    </section>
  );
}
